import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { configureStore } from "@reduxjs/toolkit";
import { AnyAction } from "redux";

import createRPCQuery from "../utils/createRPCQuery.util";
import { getPRPC } from "../hooks/usePRPC";

import rootReducer from "./reducers/index";

/**
 * One-time move of auth token from shared localStorage (persist:root.session)
 * into tab-scoped sessionStorage (persist:session), so existing tabs keep
 * their login after the upgrade instead of forcing a re-login.
 */
function migrateSessionToSessionStorage() {
	try {
		if (typeof window === "undefined") return;
		if (sessionStorage.getItem("persist:session")) return;

		const rootRaw = localStorage.getItem("persist:root");
		if (!rootRaw) return;

		const root = JSON.parse(rootRaw);
		if (!root?.session) return;

		const sessionSlice =
			typeof root.session === "string"
				? JSON.parse(root.session)
				: root.session;

		if (!sessionSlice || typeof sessionSlice !== "object") return;

		const persistSession: Record<string, string> = {};
		Object.keys(sessionSlice).forEach((key) => {
			if (key === "_persist") return;
			persistSession[key] = JSON.stringify(sessionSlice[key]);
		});
		persistSession._persist = JSON.stringify({
			version: -1,
			rehydrated: true,
		});

		sessionStorage.setItem(
			"persist:session",
			JSON.stringify(persistSession),
		);

		delete root.session;
		localStorage.setItem("persist:root", JSON.stringify(root));
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error("[persist] session migration failed", error);
	}
}

migrateSessionToSessionStorage();

const persistConfig = {
	key: "root",
	storage: {
		...storage,
		setItem: async (key, value) => {
			try {
				const prpcow = getPRPC();

				const isGetSetting = localStorage.getItem("getSetting");

				if (
					isGetSetting === "true" &&
					prpcow?.theirsModel?.dispatcher?.setFrontendStorage
				) {
					localStorage.setItem("getSetting", "false");
					const res = await createRPCQuery(() =>
						prpcow?.theirsModel.dispatcher.setFrontendStorage(
							value,
						),
					);

					if (res.storage) {
						localStorage.setItem("getSetting", "true");
						// Never let server-shared blob reintroduce a session
						// token into localStorage (would sync accounts across tabs).
						try {
							const parsed = JSON.parse(res.storage);
							if (parsed?.session) {
								delete parsed.session;
								localStorage.setItem(
									key,
									JSON.stringify(parsed),
								);
							} else {
								localStorage.setItem(key, res?.storage);
							}
						} catch {
							localStorage.setItem(key, res?.storage);
						}
					}
					return storage.setItem(key, value);
				}

				return await storage.setItem(key, value);
			} catch (error) {
				console.error(
					"[persistedReducer] [setItem] Error saving state to server:",
					error,
				);
			}

			return storage.setItem(key, value);
		},
	},
	// `session` is persisted separately in sessionStorage (see reducers/index).
	whitelist: [
		"language",
		"mapSettingsReducer",
		"settings",
		"widgets",
		"tableColumns",
		"orderPageLocalStorage",
	],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const baseStore = configureStore({
	reducer: persistedReducer,
	// Keep thunk + silence redux-persist non-serializable actions.
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: false,
			immutableCheck: false,
		}),
	devTools:
		process?.env?.NODE_ENV === "development" ||
		process?.env?.NODE_ENV === "test",
});

export type RootState = ReturnType<typeof baseStore.getState>;
/**
 * Nested persistReducer breaks configureStore's inferred thunk dispatch.
 * Force ThunkDispatch so `typeof store.dispatch` (used by map services etc.)
 * and useTypedDispatch both accept thunks.
 */
export type RootDispatch = ThunkDispatch<RootState, unknown, AnyAction>;

export const store = baseStore as Omit<typeof baseStore, "dispatch"> & {
	dispatch: RootDispatch;
};

export const persistedStore = persistStore(store);

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useTypedDispatch = () => useDispatch<RootDispatch>();
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
